import React from 'react';
import reduce from 'lodash/reduce';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import {
  BookOutline,
  CloudUpload,
  CopyOutline,
  Delete,
  EditOutline,
  EyeOutline,
  PeopleGroupOutline,
  ShareOutline,
} from '@lyearn/icons';
import { ActionMenuProps } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Avatar, Box, Stack } from '@lyearn/ui';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import {
  getAlphabeticallySortItem,
  getDefaultSortValue,
  getLastEditedSortItem,
  getNewestSortItem,
  getOldestSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import { FilterManager, SortAndFilterItem } from '@/components/FilterManager/types';
import contentConfig from '@/modules/content/config';
import renderImage from '@/modules/content/utils/renderImage';
import {
  EmptyStateLabel,
  EntityManagerLabels,
} from '@/modules/corporate/modules/admin/components/AdminEntity/types';
import { DEFAULT_GROUP_ITEM_MODE, GroupItemMode } from '@/modules/corporate/modules/group/types';
import { ShareMenuActionProps, ShareMenuActions } from '@/modules/lyearn-connect/types';
import getShareMenuItems from '@/modules/lyearn-connect/utils';
import {
  ContentCollectionGroupType,
  GroupTypeEnum,
  ResourceStatusEnum,
  UserCollectionGroupType,
  UserTypeEnum,
} from '@/types/schema';
import { grayScale } from '@/utils/styles';

import { ContentCollectionFragment } from '../graphql/fragments/generated/ContentCollection';
import { UserCollectionFragment } from '../graphql/fragments/generated/UserCollection';
import { GroupImageClassOverries, GroupMenuActions, GroupsActions } from '../types';
import { GROUP_IMAGE_CLASSES } from './consts';
import { collectionsImageBorderStyle } from './styles';

export const GROUP_TRANSLATIONS: Record<
  GroupTypeEnum,
  EntityManagerLabels & {
    title: string;
    placeholder: string;
  }
> = {
  [GroupTypeEnum.ContentCollection]: {
    title: 'Group:Collections',
    count: {
      items: 'Group:Manager.Collection',
      showingItems: 'Group:Manager.ShowingCollection',
    },
    placeholder: 'Group:Manager.SearchCollection',
  },
  [GroupTypeEnum.UserCollection]: {
    title: 'Group:Groups',
    count: {
      items: 'Group:Manager.Group',
      showingItems: 'Group:Manager.ShowingGroup',
    },
    placeholder: 'Group:Manager.SearchGroup',
  },
};

export const GROUP_EMPTY_STATE: Record<
  GroupTypeEnum,
  EmptyStateLabel & {
    title: string;
    description: string;
  }
> = {
  [GroupTypeEnum.ContentCollection]: {
    title: 'Group:Manager.NoCollectionsAvailable',
    description: 'Group:Manager.NoCollectionsDescription',
    Icon: BookOutline,
  },
  [GroupTypeEnum.UserCollection]: {
    title: 'Group:Manager.NoGroupsAvailable',
    description: 'Group:Manager.NoGroupsDescription',
    Icon: PeopleGroupOutline,
  },
};

export function getMenuItems({
  t,
  isEditable,
  showLearnerLink,
  showLyearnConnect,
}: {
  t: TFunction;
  isEditable?: boolean;
} & ShareMenuActionProps) {
  return getEnabledItems([
    {
      id: GroupMenuActions.View,
      label: t('Common:Actions.View'),
      EndIcon: EyeOutline,
      enabled: true,
    },
    {
      id: GroupMenuActions.Edit,
      label: t('Common:Actions.Edit'),
      EndIcon: EditOutline,
      enabled: isEditable,
    },
    {
      id: GroupMenuActions.Duplicate,
      label: t('Common:Actions.Duplicate'),
      EndIcon: CopyOutline,
      enabled: isEditable,
    },
    {
      id: ShareMenuActions.OpenShareMenu,
      label: t('Common:Share'),
      EndIcon: ShareOutline,
      enabled: true,
      items: getShareMenuItems({
        t,
        showLyearnConnect,
        showLearnerLink,
      }),
    },
    {
      id: GroupMenuActions.Delete,
      label: t('Common:Actions.Delete'),
      EndIcon: Delete,
      enabled: true,
      appearance: 'danger',
    },
  ]) as ActionMenuProps['items'];
}

const GROUP_SORT_ITEMS = [
  getLastEditedSortItem(),
  getAlphabeticallySortItem(),
  getNewestSortItem(),
  getOldestSortItem(),
];

export const GROUP_SORT_DEFAULT_VALUE = getDefaultSortValue(GROUP_SORT_ITEMS);

export function getSortAndFilterItems(t: TFunction): SortAndFilterItem[] {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: GROUP_SORT_ITEMS,
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'createdByIds',
      defaultPlaceholder: t('Common:Filter.CreatedBy'),
      entity: 'user',
      listConfig: UserEntityConfig,
      queryParams: {
        userTypes: [UserTypeEnum.Staff],
      },
    },
    {
      type: FilterManager.DateRange,
      id: 'createdAt',
      defaultPlaceholder: t('Common:Filter.CreatedOn'),
      isEnabled: true,
    },
  ];
}

export const getGroupImage = ({
  type,
  group,
  mode = DEFAULT_GROUP_ITEM_MODE,
  overrides,
  t,
}: {
  group?:
    | Pick<UserCollectionFragment, 'id' | 'name' | 'sampleUsers'>
    | Pick<ContentCollectionFragment, 'id' | 'sampleMembers'>;
  type: GroupTypeEnum;
  mode?: GroupItemMode;
  overrides?: GroupImageClassOverries;
  t: TFunction;
}) => {
  const classes = overrides ?? GROUP_IMAGE_CLASSES[mode];

  if (type === GroupTypeEnum.UserCollection) {
    const { root, firstPicture, secondPicture, secondPictureSize, firstPictureSize } =
      classes.userCollection;
    const userGroup = group as UserCollectionGroupType;
    const profilePictures = reduce(
      userGroup?.sampleUsers,
      (result: { imageUrl: string; name?: string | null }[], user) => {
        if (user.profilePicture) {
          result.push({ imageUrl: user.profilePicture, name: user.name });
        }
        return result;
      },
      [],
    );

    return (
      <Stack className={cn('relative flex-shrink-0', root)}>
        <Avatar
          classes={{ border: firstPicture }}
          name={t('Common:ImageOfGroupUser', {
            userName: profilePictures[1]?.name,
            groupName: userGroup?.name,
          })}
          size={firstPictureSize}
          src={profilePictures[1]?.imageUrl}
        />
        <Avatar
          classes={{ border: secondPicture }}
          name={t('Common:ImageOfGroupUser', {
            userName: profilePictures[0]?.name,
            groupName: userGroup?.name,
          })}
          size={secondPictureSize}
          src={profilePictures[0]?.imageUrl}
        />
      </Stack>
    );
  }

  const contentGroup = group as ContentCollectionGroupType;
  const { root, firstPicture, firstPictureSize, secondPicture, secondPictureSize } =
    classes.contentCollection;

  const pictures = reduce(
    contentGroup?.sampleMembers,
    (result: (JSX.Element | null)[], content) => {
      if (Object.keys(content).length) {
        const { image } = contentConfig[content.type].icons?.getContentImage(content)!;
        const renderedImage = renderImage({
          image,
          contentName: content.name,
          className: cn(
            'w-full h-full text-s-info-light rounded-4',
            collectionsImageBorderStyle,
            [ResourceStatusEnum.Archived, ResourceStatusEnum.Revoked].includes(content.status)
              ? grayScale
              : '',
          ),
          width: result.length ? firstPictureSize : secondPictureSize,
          height: result.length ? firstPictureSize : secondPictureSize,
        });

        if (renderedImage) {
          result.push(
            <Box className={result.length ? firstPicture : secondPicture}>{renderedImage}</Box>,
          );
        }
      }
      return result;
    },
    [],
  );

  return (
    <Stack alignItems="center" className={cn('relative flex-shrink-0', root)}>
      {pictures[1] || <Stack className={firstPicture} />}
      {pictures[0] || <Stack className={secondPicture} />}
    </Stack>
  );
};

export const getActionMenuItems = (t: TFunction) => {
  return [
    {
      id: GroupsActions.BulkImport,
      label: t('Group:BulkImport.ImportGroups'),
      EndIcon: CloudUpload,
    },
  ] as ActionMenuProps['items'];
};
